import { useEffect, useState } from 'react'

// Client-side personalization — favourites, recently-viewed lanes, and the remembered container box.
// All in localStorage so it works instantly for every visitor with no login (a signed-in sync can
// layer on later). Changes broadcast on a window event so every widget re-renders in step, and the
// native `storage` event keeps other tabs consistent.

export interface FavLane {
  pol: string
  pod: string
  polName?: string | null
  podName?: string | null
}

const FAVS = 'frm.favs'
const RECENT = 'frm.recent'
const BOX = 'frm.box'
const PINNED = 'frm.pinned'
const LANESORT = 'frm.lanesort'
const COLS = 'frm.cols'
const HIDELINERS = 'frm.hideliners'
const CARGO = 'frm.cargo'
const EVT = 'frm-personal'
const RECENT_MAX = 8

/** Regional-indicator flag from a UN/LOCODE (first two letters = country). '' if not derivable. */
export function flagFor(code?: string | null): string {
  const cc = (code ?? '').slice(0, 2).toUpperCase()
  if (!/^[A-Z]{2}$/.test(cc)) return ''
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65))
}

const laneKey = (pol: string, pod: string) => `${pol}-${pod}`.toUpperCase()

function read<T>(key: string, fallback: T): T {
  if (typeof localStorage === 'undefined') return fallback
  try {
    const raw = localStorage.getItem(key)
    return raw ? (JSON.parse(raw) as T) : fallback
  } catch {
    return fallback
  }
}
function write(key: string, val: unknown): void {
  try {
    localStorage.setItem(key, JSON.stringify(val))
  } catch {}
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(EVT))
}

/** Subscribe to any personalization change (this tab via CustomEvent, other tabs via storage). */
function subscribe(cb: () => void): () => void {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(EVT, cb)
  window.addEventListener('storage', cb)
  return () => {
    window.removeEventListener(EVT, cb)
    window.removeEventListener('storage', cb)
  }
}

// ── Favourites ──────────────────────────────────────────────────────────────
export const readFavs = (): FavLane[] => read<FavLane[]>(FAVS, [])
export const isFav = (pol: string, pod: string): boolean =>
  readFavs().some((f) => laneKey(f.pol, f.pod) === laneKey(pol, pod))

/** Add/remove a lane. Returns the new list. */
export function toggleFav(lane: FavLane): FavLane[] {
  const k = laneKey(lane.pol, lane.pod)
  const cur = readFavs()
  const next = cur.some((f) => laneKey(f.pol, f.pod) === k)
    ? cur.filter((f) => laneKey(f.pol, f.pod) !== k)
    : [{ pol: lane.pol, pod: lane.pod, polName: lane.polName, podName: lane.podName }, ...cur]
  write(FAVS, next)
  return next
}

// ── Pinned lanes (the favourites that stay on top of the board) and the board's sort ──────────────
export const readPinned = (): string[] => read<string[]>(PINNED, [])
/** Pin or unpin a lane by its "POL-POD" id. Returns the new list. */
export function togglePinned(id: string): string[] {
  const key = id.toUpperCase()
  const cur = readPinned()
  const next = cur.includes(key) ? cur.filter((x) => x !== key) : [...cur, key]
  write(PINNED, next)
  return next
}
export const readCols = (): unknown => read<unknown>(COLS, null)
export const setCols = (c: string[]): void => write(COLS, c)
export const readHiddenLiners = (): unknown => read<unknown>(HIDELINERS, [])
export const setHiddenLiners = (l: string[]): void => write(HIDELINERS, l)
export interface Cargo {
  cbm: number
  kg: number
}
export const readCargo = (): Cargo => {
  const c = read<Partial<Cargo>>(CARGO, {})
  return { cbm: Number(c.cbm) > 0 ? Number(c.cbm) : 2, kg: Number(c.kg) >= 0 ? Number(c.kg) : 800 }
}
export const setCargo = (c: Cargo): void => write(CARGO, c)
export const getLaneSort = (): string => read<string>(LANESORT, 'added')
export const setLaneSort = (m: string): void => write(LANESORT, m)

// ── Recently viewed ─────────────────────────────────────────────────────────
export const readRecent = (): FavLane[] => read<FavLane[]>(RECENT, [])
export function recordRecent(lane: FavLane): void {
  const k = laneKey(lane.pol, lane.pod)
  const next = [
    { pol: lane.pol, pod: lane.pod, polName: lane.polName, podName: lane.podName },
    ...readRecent().filter((f) => laneKey(f.pol, f.pod) !== k),
  ].slice(0, RECENT_MAX)
  write(RECENT, next)
}

// ── Remembered container box ─────────────────────────────────────────────────
export const getBox = (): string => read<string>(BOX, '40hc')
export const setBox = (b: string): void => write(BOX, b)

// ── Hero theme (deep-ink vs light) — a header toggle, remembered across visits ──
export type HeroTheme = 'dark' | 'light'
const THEME = 'frm.herotheme'
export const getHeroTheme = (): HeroTheme => read<HeroTheme>(THEME, 'light')
export const setHeroTheme = (t: HeroTheme): void => write(THEME, t)

// ── React hooks ──────────────────────────────────────────────────────────────
function useStore<T>(getter: () => T): T {
  const [v, setV] = useState<T>(getter)
  useEffect(() => {
    setV(getter())
    return subscribe(() => setV(getter()))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return v
}

export const useFavs = (): FavLane[] => useStore(readFavs)
export const useRecent = (): FavLane[] => useStore(readRecent)
export const useBox = (): string => useStore(getBox)
export const usePinned = (): string[] => useStore(readPinned)
export const useLaneSort = (): string => useStore(getLaneSort)
export const useCols = (): unknown => useStore(readCols)
export const useCargo = (): Cargo => useStore(readCargo)
export const useHiddenLiners = (): unknown => useStore(readHiddenLiners)
export const useHeroTheme = (): HeroTheme => useStore(getHeroTheme)
